import {
  Component, ElementRef, Injector, Input, ViewChild
} from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  map, Observable, startWith, tap
} from 'rxjs';

import { BaseControlValueAccessor, IIrisFormFieldControl } from '@sondermind/iris/form-helpers';

export enum TextareaSizes {
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg'
}

@Component({
  selector: 'iris-text-area',
  templateUrl: './text-area.component.html',
  styleUrls: ['./text-area.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: IrisTextAreaComponent,
    },
  ],
  standalone: false
})
export class IrisTextAreaComponent
  extends BaseControlValueAccessor
  implements IIrisFormFieldControl {
  constructor(injector: Injector) { super(injector) }

  @Input() autoResizeEnabled: boolean = false;
  @Input() manualResizeEnabled: boolean = false;
  @Input() placeholder: string = '';
  @Input() size: TextareaSizes;
  @Input() maxCharacterCount: number;
  @Input() isReadonly: boolean = false;
  @Input() hasIcon: boolean = false;

  resizeObserver: ResizeObserver;
  textareaClasses: string;

  @ViewChild('textarea') textarea: ElementRef;
  @ViewChild('animationContainer') animationContainer: ElementRef;

  currentCount$: Observable<number>;

  ngOnInit(): void {
    const sizingClass = this.size ? ` iris-textarea--${this.size}` : '';
    const resizeClass = !this.manualResizeEnabled ? ' non-resizable' : '';

    this.textareaClasses = `iris-textarea${sizingClass}${resizeClass}`;

    if (sizingClass) {
      this.autoResizeEnabled = true;
    }

    if (this.maxCharacterCount > 0) {
      /* If the character counter is enabled then mark the
      text area as touched as soon as the user starts typing.
      This ensures that max length validation will occur eagerly
      as the user types instead of lazily on blur. */
      this.currentCount$ = this.control.valueChanges.pipe(
        map((val: string) => val.length),
        tap(() => {
          if (this.control.untouched) {
            this.control.markAsTouched({ onlySelf: true });
          }
        }),
        startWith(0)
      );
    }
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.contentBoxSize[0]) {
          this.animationContainer.nativeElement.style.width = `${this.textarea.nativeElement.style.width}`;
        }
      });
    });

    this.resizeObserver.observe(this.textarea.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver.unobserve(this.textarea.nativeElement);
    this.resizeObserver.disconnect();
  }

  autoResize(): void {
    if (this.autoResizeEnabled) {
      this.textarea.nativeElement.style.height = '0';
      const { scrollHeight } = this.textarea.nativeElement;

      this.textarea.nativeElement.style.height = `${scrollHeight}px`;
    }
  }

  override get control(): FormControl {
    return this.formControl || this.controlContainer.control.get(this.formControlName) as FormControl;
  }
}
